import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Reveal, SectionLabel, MaskLines } from "./Reveal";
import { api, apiError } from "../../lib/api";
import { trackConversion } from "../../lib/tracking";
import { useContent } from "../../lib/content";

const SECTORS = ["Pompe di calore", "Climatizzazione", "Fotovoltaico", "Termoidraulica", "Più settori"];
const EMPTY = { full_name: "", company: "", phone: "", email: "", sector: "", area: "", website: "" };

const Field = ({ id, label, ...p }) => (
  <label className="block group">
    <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-2 group-focus-within:text-[#7fd6fb] transition-colors">{label}</span>
    <input id={id} data-testid={`form-${id}`} {...p}
      className="w-full h-12 rounded-xl bg-white/[0.03] border hairline px-4 text-white placeholder:text-slate-500 outline-none focus:border-[#4fc3f7]/60 focus:bg-white/[0.05] transition-colors" />
  </label>
);

export const ConsultForm = () => {
  const { home: h, tracking } = useContent();
  const [f, setF] = useState(EMPTY);
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.sector) return toast.error("Seleziona il settore della tua azienda");
    if (!privacy) return toast.error("Accetta l'informativa privacy per continuare");
    setLoading(true);
    try {
      await api.post("/leads", { ...f, privacy });
      trackConversion(tracking, { form: "consulenza", sector: f.sector }, { email: f.email, phone: f.phone, full_name: f.full_name });
      setDone(true);
      setF(EMPTY);
      setPrivacy(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="consulenza" data-testid="cta-section" className="relative py-24 sm:py-32 lg:py-40 overflow-hidden noise">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_20%,rgba(255,85,0,0.16),transparent_55%),radial-gradient(ellipse_at_90%_80%,rgba(59,110,240,0.2),transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <Reveal><SectionLabel tone="heat">{h.cta_label}</SectionLabel></Reveal>
          <h2 className="mt-7 font-display font-extrabold text-white text-[2.2rem] sm:text-5xl lg:text-[3.8rem] leading-[1]">
            <MaskLines text={h.cta_title.replace(". ", ".\n")} lineClass={(i) => (i === 1 ? "text-heat-grad" : "")} />
          </h2>
          <Reveal delay={0.1}><p className="mt-7 max-w-lg text-base sm:text-lg text-slate-300 leading-relaxed">{h.cta_subtitle}</p></Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-6">
          <div className="relative glass rounded-3xl p-5 sm:p-8">
            {done ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="form-success" className="py-14 text-center">
                <CheckCircle2 className="w-14 h-14 mx-auto text-[#4fc3f7]" />
                <p className="mt-6 font-display font-extrabold text-2xl text-white">Richiesta ricevuta</p>
                <p className="mt-3 text-slate-300 max-w-sm mx-auto">{h.form_success}</p>
                <button data-testid="form-new-request" onClick={() => setDone(false)} className="mt-8 text-sm text-slate-300 underline underline-offset-4 hover:text-white">Invia un'altra richiesta</button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="consult-form" className="grid sm:grid-cols-2 gap-4">
                <Field id="full_name" label="Nome e cognome" required minLength={2} value={f.full_name} onChange={set("full_name")} autoComplete="name" />
                <Field id="company" label="Azienda" required value={f.company} onChange={set("company")} autoComplete="organization" />
                <Field id="phone" label="Telefono" type="tel" required minLength={6} value={f.phone} onChange={set("phone")} autoComplete="tel" />
                <Field id="email" label="Email" type="email" required value={f.email} onChange={set("email")} autoComplete="email" />
                <label className="block">
                  <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-2">Settore</span>
                  <Select value={f.sector} onValueChange={(v) => setF((x) => ({ ...x, sector: v }))}>
                    <SelectTrigger data-testid="form-sector" className="h-12 rounded-xl bg-white/[0.03] border hairline text-white">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0e1528] border hairline text-white">
                      {SECTORS.map((s) => <SelectItem key={s} value={s} data-testid={`form-sector-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </label>
                <Field id="area" label="Area geografica" required minLength={2} placeholder="Es. Napoli e provincia" value={f.area} onChange={set("area")} />
                <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
                <label className="sm:col-span-2 flex items-start gap-3 text-[13px] text-slate-300 mt-1 cursor-pointer">
                  <Checkbox data-testid="form-privacy" checked={privacy} onCheckedChange={(v) => setPrivacy(!!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#ff5500] data-[state=checked]:border-[#ff5500]" />
                  <span>Ho letto l'<Link to="/privacy" className="underline underline-offset-2 hover:text-white">informativa privacy</Link> e acconsento al trattamento dei dati per essere ricontattato.</span>
                </label>
                <button type="submit" disabled={loading} data-testid="form-submit"
                  className="sm:col-span-2 group mt-2 h-14 rounded-xl bg-[#ff5500] hover:bg-[#ff6a1f] disabled:opacity-60 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors shadow-[0_10px_40px_-12px_rgba(255,85,0,0.8)]">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{h.cta_button}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
